import moment from 'moment'

export const useValidationsRules = (): object => {
  const isValidPesel = (pesel: string): boolean => {
    if (typeof pesel !== 'string') return false

    const controlNumber = parseInt(pesel.substring(10, 11))
    const weight = [1, 3, 7, 9, 1, 3, 7, 9, 1, 3]
    let sum = 0

    for (let i = 0; i < weight.length; i++) {
      sum += parseInt(pesel.substring(i, i + 1)) * weight[i]
    }
    sum = sum % 10
    return (10 - sum) % 10 === controlNumber
  }

  const isValidNip = (nip: string): boolean => {
    if (typeof nip !== 'string') return false
    // Remove spaces and hyphens
    const cleaned = nip.replace(/[\s-]/g, '')
    if (!/^[0-9]{10}$/.test(cleaned)) return false

    const weights = [6, 5, 7, 2, 3, 4, 5, 6, 7]
    const sum = weights.reduce((acc, w, i) => acc + parseInt(cleaned[i]) * w, 0)
    const remainder = sum % 11

    return remainder !== 10 && remainder === parseInt(cleaned[9])
  }

  const hasAgeBetween = (
    date: string,
    minAgeInMonths: number,
    maxAgeInMonths: number
  ): boolean => {
    const ageInMonths = getAgeInMonths(date)

    return ageInMonths >= minAgeInMonths && ageInMonths <= maxAgeInMonths
  }

  const hasAgeGreaterThan = (date: string, minAge: number): boolean => {
    const age = getAgeFromDate(date)

    return age >= minAge
  }

  const hasPeselAgeBetween = (
    pesel: string,
    minAge: number,
    maxAge: number
  ): boolean => {
    if (pesel.length !== 11) return false

    const age = getAgeFromPesel(pesel)

    return age >= minAge && age < maxAge
  }

  const hasPeselAgeGreaterThan = (pesel: string, minAge: number): boolean => {
    if (pesel.length !== 11) return false

    const age = getAgeFromPesel(pesel)

    return age >= minAge
  }

  return {
    isValidPesel,
    isValidNip,
    hasAgeBetween,
    hasAgeGreaterThan,
    hasPeselAgeBetween,
    hasPeselAgeGreaterThan,
  }
}

const getAgeFromDate = (date: string) => {
  const today = new Date()
  const birthDate = new Date(date)

  const years = today.getFullYear() - birthDate.getFullYear()
  const months = today.getMonth() - birthDate.getMonth()

  let totalMonths = years * 12 + months

  if (today.getDate() < birthDate.getDate()) {
    totalMonths--
  }

  return totalMonths
}

const getAgeFromPesel = (pesel: string): number => {
  const today = new Date()
  const dateFromPesel = getBirthDateFromPesel(pesel)
  const birthDate = new Date(dateFromPesel.replace(/-/g, '/'))

  let ageInMonths =
    (today.getFullYear() - birthDate.getFullYear()) * 12 +
    (today.getMonth() - birthDate.getMonth())

  if (today.getDate() < birthDate.getDate()) {
    ageInMonths--
  }

  return ageInMonths
}

const getBirthDateFromPesel = (pesel: string): string => {
  const peselInt = pesel.split('').map(Number)
  let year = 1900 + peselInt[0] * 10 + peselInt[1]

  if (peselInt[2] >= 2 && peselInt[2] < 8) {
    year += Math.floor(peselInt[2] / 2) * 100
  }

  if (peselInt[2] >= 8) {
    year -= 100
  }

  const month = (peselInt[2] % 2) * 10 + peselInt[3]
  const day = peselInt[4] * 10 + peselInt[5]

  return moment({ year, month: month - 1, day }).format('YYYY-MM-DD')
}

const getAgeInMonths = (date: string) => {
  const now = new Date()
  const birthDate = new Date(date)

  const yearsDiff = now.getFullYear() - birthDate.getFullYear()
  const monthsDiff = now.getMonth() - birthDate.getMonth()

  return yearsDiff * 12 + monthsDiff
}
